'use client';
import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import BookingCalendar from './BookingCalendar';
import TeeSheetSelector from './TeeSheetSelector';
import BookingDetails from './BookingDetails';
import VisitorRegistrationForm from './VisitorRegistrationForm';
import ConsentStep from './ConsentStep';
import PaymentStep from './PaymentStep';
import BookingConfirmation from './BookingConfirmation';
import { trackBookingStart, trackBookingStep } from '@/lib/analytics';

export interface BookingState {
  selectedDate: string;
  selectedSlot: string;
  selectedTime: string;
  holes: 9 | 18;
  players: number;
  cart: 'none' | 'single' | 'double';
  playerNames: string[];
  greenFees: number;
  cartFees: number;
  total: number;
  visitorData: Record<string, string | boolean>;
  consentData: Record<string, boolean>;
  paymentMethod: string;
}

const STEPS = [
  { id: 1, label: 'Date', icon: 'CalendarDaysIcon' },
  { id: 2, label: 'Tee Time', icon: 'ClockIcon' },
  { id: 3, label: 'Details', icon: 'DocumentTextIcon' },
  { id: 4, label: 'Register', icon: 'UserPlusIcon' },
  { id: 5, label: 'Consent', icon: 'ShieldCheckIcon' },
  { id: 6, label: 'Payment', icon: 'CreditCardIcon' },
];

const initialState: BookingState = {
  selectedDate: '',
  selectedSlot: '',
  selectedTime: '',
  holes: 18,
  players: 2,
  cart: 'none',
  playerNames: [],
  greenFees: 0,
  cartFees: 0,
  total: 0,
  visitorData: {},
  consentData: {},
  paymentMethod: '',
};

export default function BookingWizard() {
  const [step, setStep] = useState(1);
  const [booking, setBooking] = useState<BookingState>(initialState);
  const [confirmed, setConfirmed] = useState(false);

  const updateBooking = (updates: Partial<BookingState>) => {
    setBooking((prev) => ({ ...prev, ...updates }));
  };

  const STEP_LABELS = ['Date', 'Tee Time', 'Details', 'Register', 'Consent', 'Payment'];

  const goNext = () => {
    const nextStep = Math.min(step + 1, 6);
    if (step === 1 && nextStep === 2) trackBookingStart();
    trackBookingStep(nextStep, STEP_LABELS[nextStep - 1]);
    setStep(nextStep);
  };
  const goBack = () => setStep((s) => Math.max(s - 1, 1));

  if (confirmed) {
    return <BookingConfirmation booking={booking} />;
  }

  const currentStepData = STEPS.find((s) => s.id === step);

  return (
    <div className="max-w-screen-2xl mx-auto px-3 sm:px-4 lg:px-8 xl:px-10 py-4 sm:py-8">
      {/* Step Indicator */}
      <div className="mb-6 sm:mb-8">
        {/* Mobile: compact step counter + label */}
        <div className="flex sm:hidden items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center step-active">
              <Icon name={currentStepData?.icon as Parameters<typeof Icon>[0]['name']} size={14} variant="outline" />
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Step {step} of {STEPS.length}</div>
              <div className="text-sm font-semibold text-primary">{currentStepData?.label}</div>
            </div>
          </div>
          <div className="flex gap-1">
            {STEPS.map((s) => (
              <div
                key={`dot-${s.id}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  s.id < step ? 'w-4 bg-secondary' : s.id === step ? 'w-6 bg-primary' : 'w-1.5 bg-border'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Desktop: full step indicator */}
        <div className="hidden sm:flex items-center justify-between relative">
          <div className="absolute left-0 right-0 top-5 h-0.5 bg-border -z-0" />
          {STEPS.map((s) => (
            <div key={`step-${s.id}`} className="flex flex-col items-center gap-2 z-10">
              <button
                onClick={() => s.id < step && setStep(s.id)}
                className={`w-10 h-10 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                  s.id < step
                    ? 'step-completed cursor-pointer'
                    : s.id === step
                    ? 'step-active' :'step-inactive cursor-default'
                }`}
                disabled={s.id > step}
                aria-label={`Step ${s.id}: ${s.label}`}
              >
                {s.id < step ? (
                  <Icon name="CheckIcon" size={16} variant="outline" />
                ) : (
                  <Icon name={s.icon as Parameters<typeof Icon>[0]['name']} size={16} variant="outline" />
                )}
              </button>
              <span className={`text-xs font-medium hidden sm:block ${
                s.id === step ? 'text-primary' : s.id < step ? 'text-secondary' : 'text-muted-foreground'
              }`}>
                {s.label}
              </span>
            </div>
          ))}
        </div>

        {/* Progress bar */}
        <div className="mt-3 sm:mt-4 h-1.5 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full gradient-primary rounded-full transition-all duration-500"
            style={{ width: `${((step - 1) / (STEPS.length - 1)) * 100}%` }}
          />
        </div>
      </div>

      {/* Step Content */}
      <div className="fade-in">
        {step === 1 && (
          <BookingCalendar
            booking={booking}
            updateBooking={updateBooking}
            onNext={goNext}
          />
        )}
        {step === 2 && (
          <TeeSheetSelector
            booking={booking}
            updateBooking={updateBooking}
            onNext={goNext}
            onBack={goBack}
          />
        )}
        {step === 3 && (
          <BookingDetails
            booking={booking}
            updateBooking={updateBooking}
            onNext={goNext}
            onBack={goBack}
          />
        )}
        {step === 4 && (
          <VisitorRegistrationForm
            booking={booking}
            updateBooking={updateBooking}
            onNext={goNext}
            onBack={goBack}
          />
        )}
        {step === 5 && (
          <ConsentStep
            booking={booking}
            updateBooking={updateBooking}
            onNext={goNext}
            onBack={goBack}
          />
        )}
        {step === 6 && (
          <PaymentStep
            booking={booking}
            updateBooking={updateBooking}
            onBack={goBack}
            onConfirm={() => setConfirmed(true)}
          />
        )}
      </div>
    </div>
  );
}